// src/pages/London.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "./London.css"; // adjust path/name if needed
import api from "../utils/api";

const London = () => {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  useEffect(() => {
    let mounted = true;

    api
      .get("/api/events/all")
      .then((res) => {
        if (!mounted) return;
        const list = Array.isArray(res.data?.events) ? res.data.events : [];
        // Basic future-only + sort by nearest date
        const cleaned = list
          .filter((ev) => ev.date && new Date(ev.date) >= new Date())
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 6);
        setEvents(cleaned);
        setLoadingEvents(false);
      })
      .catch(() => {
        if (!mounted) return;
        setLoadingEvents(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="london-page">
      <Helmet>
        <title>
          London Taxi & Airport Transfers – Cabs, Minibus & Coach Hire | CheapCabGrab
        </title>
        <meta
          name="description"
          content="Plan London taxi and airport transfers with CheapCabGrab. Fixed-price cabs, minibus hire and coach hire for Heathrow, Gatwick, Luton, Stansted, stations, hotels and London attractions."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/london" />
        <meta
          name="keywords"
          content="London taxi, Heathrow to London taxi, London airport transfers, London minibus hire, London coach hire, Wembley taxi, O2 Arena taxi, Central London cab, CheapCabGrab"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="London Taxi & Airport Transfers – Cabs, Minibus & Coach Hire"
        />
        <meta
          property="og:description"
          content="Book London taxis, minibus hire and coach hire for Heathrow, Gatwick, Luton, Stansted and London events with CheapCabGrab."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/london" />

        {/* JSON-LD – TaxiService focused on London */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TaxiService",
            name: "CheapCabGrab – London Taxi, Minibus & Coach Hire",
            url: "https://www.cheapcabgrab.com/london",
            areaServed: [
              "Central London",
              "West London",
              "North London",
              "South London",
              "East London",
              "Heathrow Airport",
              "Gatwick Airport",
              "Luton Airport",
              "Stansted Airport",
            ],
            serviceArea: {
              "@type": "AdministrativeArea",
              name: "Greater London",
            },
            provider: {
              "@type": "Organization",
              name: "CheapCabGrab",
              url: "https://www.cheapcabgrab.com",
            },
            sameAs: [
              "https://www.cheapcabgrab.com/heathrow-taxi",
              "https://www.cheapcabgrab.com/minibus-hire",
              "https://www.cheapcabgrab.com/coach-hire",
            ],
          })}
        </script>
      </Helmet>

      {/* 🔹 TOP STRIP */}
      <div className="london-top-strip">
        <div className="london-top-strip-inner">
          <span className="london-strip-label">London Travel Guide</span>
          <span className="london-strip-item">
            🚖 Fixed-price taxis between London, Heathrow, Gatwick, Luton & Stansted.
          </span>
          <span className="london-strip-item">
            🏙 CheapCabGrab covers Central, West, North, South & East London.
          </span>
          <span className="london-strip-item">
            🚐 Minibus & coach hire for events, stadiums and group travel.
          </span>
        </div>
      </div>

      <main className="london-main">
        {/* 🔹 HERO / INTRO */}
        <section className="london-hero" aria-labelledby="london-hero-heading">
          <div className="london-hero-text">
            <h1 id="london-hero-heading">
              London Taxi & Airport Transfers – Complete Guide
            </h1>
            <p>
              London is one of the world’s busiest cities for airports, rail stations,
              stadiums and hotels. This guide shows how CheapCabGrab can help you move
              smoothly between London, Heathrow and the other main airports.
            </p>
            <p>
              When you are ready to book, you can use our{" "}
              <Link to="/heathrow-taxi">Heathrow taxi page</Link>, our{" "}
              <Link to="/minibus-hire">minibus hire</Link> page or{" "}
              <Link to="/coach-hire">coach hire</Link> page for group travel.
            </p>
            <ul className="london-hero-list">
              <li>
                ✅ Fixed-price airport transfers – no surge pricing, no meter surprises.
              </li>
              <li>
                ✅ Licensed drivers, flight tracking and clear pickup instructions.
              </li>
              <li>
                ✅ Minibus hire (7–14 seats) and coach hire (25+ seats) for events and tours.
              </li>
              <li>
                ✅ Coverage across Central London, West London, North, South and East London.
              </li>
            </ul>
          </div>

          <aside className="london-hero-side">
            <div className="london-hero-card">
              <h2>Need a London taxi or minibus?</h2>
              <p>
                For quick booking, start with our Heathrow and London airport routes:
              </p>
              <ul>
                <li>
                  ✈️ <Link to="/heathrow-to-london">Heathrow → London taxi guide</Link>
                </li>
                <li>
                  🚖 <Link to="/heathrow-taxi">Heathrow Taxi – CheapCabGrab</Link>
                </li>
                <li>
                  🚐 <Link to="/heathrow-minibus-hire">Heathrow Minibus Hire</Link>
                </li>
                <li>
                  🚌 <Link to="/heathrow-coach-hire">Heathrow Coach Hire</Link>
                </li>
              </ul>
              <Link to="/minibus-hire" className="london-primary-btn">
                Book Minibus or Coach →
              </Link>
            </div>

            <div className="london-hero-links">
              <h3>Useful London tools</h3>
              <ul>
                <li>
                  🛬 <Link to="/terminalFinder">Terminal Finder</Link>
                </li>
                <li>
                  ☎️ <Link to="/airlineContacts">Airline Contacts</Link>
                </li>
                <li>
                  🚗 <Link to="/fleet">Our Fleet</Link>
                </li>
                <li>
                  👨‍✈️ <Link to="/our-drivers">Our Drivers</Link>
                </li>
                <li>
                  ❓ <Link to="/faq">FAQ</Link>
                </li>
              </ul>
            </div>
          </aside>
        </section>

        {/* 🔹 LONDON AREAS OVERVIEW */}
        <section className="london-section" id="london-areas">
          <header className="london-section-header">
            <h2>🏙 London at a Glance – Key Areas We Cover</h2>
            <p>
              CheapCabGrab covers all major parts of Greater London – ideal for airport
              runs, hotel transfers, events and business travel.
            </p>
          </header>

          <div className="london-grid london-grid-3">
            <article className="london-card">
              <h3>Central London</h3>
              <ul>
                <li>📍 Westminster, Soho, Mayfair, Covent Garden</li>
                <li>🏛 Landmarks: Big Ben, London Eye, Trafalgar Square</li>
                <li>🚖 Popular routes: hotels ↔ Heathrow, Gatwick & City Airport</li>
              </ul>
            </article>

            <article className="london-card">
              <h3>West London</h3>
              <ul>
                <li>📍 Ealing, Hammersmith, Hounslow, Southall, Wembley</li>
                <li>🏟 Close to Wembley Stadium & Heathrow corridor</li>
                <li>🚐 Ideal for minibus hire to airports and events</li>
              </ul>
            </article>

            <article className="london-card">
              <h3>North London</h3>
              <ul>
                <li>📍 Islington, Camden, Finchley, Tottenham</li>
                <li>⚽ Trips to Emirates Stadium & Tottenham Hotspur Stadium</li>
                <li>🚖 Evening and match-day taxi options available</li>
              </ul>
            </article>

            <article className="london-card">
              <h3>East London</h3>
              <ul>
                <li>📍 Stratford, Canary Wharf, Hackney, Docklands</li>
                <li>🛫 Convenient for London City Airport and ExCeL</li>
                <li>🚐 Minibus and coach hire for conferences and trade shows</li>
              </ul>
            </article>

            <article className="london-card">
              <h3>South London</h3>
              <ul>
                <li>📍 Croydon, Brixton, Clapham, Wimbledon</li>
                <li>🎾 Trips to Wimbledon, O2 Arena and South London venues</li>
                <li>🚖 Late-night and early-morning airport transfers</li>
              </ul>
            </article>

            <article className="london-card">
              <h3>Hotels & Business Districts</h3>
              <ul>
                <li>🏨 City, West End, Canary Wharf & Heathrow hotel belts</li>
                <li>🏢 Corporate travel for meetings, teams and events</li>
                <li>
                  🚌 Minibus/coach via{" "}
                  <Link to="/minibus-hire">minibus hire</Link> and{" "}
                  <Link to="/coach-hire">coach hire</Link>
                </li>
              </ul>
            </article>
          </div>
        </section>

        {/* 🔹 AIRPORT TRANSFERS */}
        <section className="london-section" id="london-airports">
          <header className="london-section-header">
            <h2>✈ London Airport Transfers – Heathrow, Gatwick, Luton & Stansted</h2>
            <p>
              Whether you are flying in or out, CheapCabGrab specialises in London to
              airport taxi, minibus and coach transfers.
            </p>
          </header>

          <div className="london-grid london-grid-2">
            <article className="london-card london-airport-card">
              <h3>London ↔ Heathrow</h3>
              <ul>
                <li>🚖 Fixed-price taxis between Central London and all Heathrow terminals.</li>
                <li>⏱ Typical time: around 35–70 minutes depending on area & traffic.</li>
                <li>
                  🔗 See our{" "}
                  <Link to="/heathrow-to-london">Heathrow → London route guide</Link>{" "}
                  and <Link to="/heathrow-taxi">Heathrow Taxi page</Link>.
                </li>
              </ul>
            </article>

            <article className="london-card london-airport-card">
              <h3>London ↔ Gatwick, Luton & Stansted</h3>
              <ul>
                <li>🛣 Direct runs via M25, M1 and M11 corridors.</li>
                <li>
                  🚐 Very popular with groups using{" "}
                  <Link to="/minibus-hire">minibus hire</Link> or{" "}
                  <Link to="/coach-hire">coach hire</Link>.
                </li>
                <li>💼 Ideal for business teams, tours and family holidays.</li>
              </ul>
            </article>

            <article className="london-card london-airport-card">
              <h3>London City & Other Airports</h3>
              <ul>
                <li>🛫 London City Airport for financial district and Canary Wharf.</li>
                <li>✈ Regional airports and private terminals on request.</li>
                <li>
                  ☎️ Contact our <Link to="/contact">support team</Link> for tailored quotes.
                </li>
              </ul>
            </article>

            <article className="london-card london-airport-card">
              <h3>Minibus & Coach Transfers</h3>
              <ul>
                <li>🚐 7–14 seat minibuses for group transfers from London hotels.</li>
                <li>🚌 25+ seat coaches via our coach partners for larger groups.</li>
                <li>
                  🔗 Learn more: <Link to="/minibus-hire">Minibus Hire</Link> &{" "}
                  <Link to="/coach-hire">Coach Hire</Link>.
                </li>
              </ul>
            </article>
          </div>
        </section>

        {/* 🔹 STATIONS & LANDMARKS */}
        <section className="london-section" id="london-stations">
          <header className="london-section-header">
            <h2>🚆 London Stations, Landmarks & Stadiums</h2>
            <p>
              Many journeys start or end at a London rail station, landmark or stadium.
              CheapCabGrab supports station pickups, hotel drops and event transfers.
            </p>
          </header>

          <div className="london-grid london-grid-3">
            <article className="london-card">
              <h3>Major London Stations</h3>
              <ul>
                <li>🚆 Paddington – key link for Heathrow Express & Elizabeth Line.</li>
                <li>🚆 King’s Cross / St Pancras – national & Eurostar links.</li>
                <li>🚆 Victoria, Waterloo, Euston & Liverpool Street.</li>
                <li>💡 Book taxis for onward journeys to hotels and airports.</li>
              </ul>
            </article>

            <article className="london-card">
              <h3>Key Landmarks & Attractions</h3>
              <ul>
                <li>🎡 London Eye, Tower of London, Buckingham Palace.</li>
                <li>🏛 British Museum, Natural History Museum, Tate Modern.</li>
                <li>
                  🚖 Use CheapCabGrab for multi-stop family days out or hotel pickups.
                </li>
                <li>
                  🔗 See also:{" "}
                  <Link to="/heathrow-attractions">Heathrow & London attractions guide</Link>.
                </li>
              </ul>
            </article>

            <article className="london-card">
              <h3>Stadiums & Arenas</h3>
              <ul>
                <li>🏟 Wembley Stadium & OVO Arena Wembley.</li>
                <li>🎵 The O2 Arena, Emirates Stadium, Tottenham Hotspur Stadium.</li>
                <li>
                  🚐 Minibus & coach hire for event nights and group outings from London
                  hotels.
                </li>
              </ul>
            </article>
          </div>
        </section>

        {/* 🔹 LONDON EVENTS (DYNAMIC FEED) */}
        <section className="london-section" id="london-events">
          <header className="london-section-header">
            <h2>🎉 Upcoming London Events – Book a Taxi or Minibus</h2>
            <p>
              Here are a few upcoming London events where passengers often use
              CheapCabGrab for travel. Always check the event website for the latest
              details.
            </p>
          </header>

          {loadingEvents && <p>Loading London events…</p>}

          {!loadingEvents && events.length === 0 && (
            <p>No events available right now – please check back soon.</p>
          )}

          {!loadingEvents && events.length > 0 && (
            <div className="london-events-grid">
              {events.map((ev, idx) => (
                <article key={idx} className="london-event-card">
                  <h3>{ev.name}</h3>
                  {ev.date && <p>📅 {ev.date}</p>}
                  {ev.venue && <p>📍 {ev.venue}</p>}
                  <div className="london-event-actions">
                    {ev.url && (
                      <a
                        href={ev.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="london-event-link"
                      >
                        🎟 View Event
                      </a>
                    )}
                    <Link to="/minibus-hire" className="london-event-taxi-link">
                      🚐 Minibus / Taxi for this event
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* 🔹 SUPPORT / CONTACT */}
        <section className="london-section" id="london-help">
          <header className="london-section-header">
            <h2>📞 Need Help Planning Your London Journey?</h2>
          </header>
          <p>
            If you are unsure whether a taxi, minibus or coach is best for your group,
            our team can help you choose the right option and route.
          </p>
          <p>
            Contact us via our <Link to="/contact">support page</Link>, or email{" "}
            <strong>support@cheapcabgrab.com</strong>.
          </p>
          <p>
            For terminal and airline details, try our{" "}
            <Link to="/terminalFinder">Terminal Finder</Link> and{" "}
            <Link to="/airlineContacts">Airline Contacts</Link> tools.
          </p>
        </section>
      </main>

      {/* FLOATING CTA – DESKTOP */}
      <button
        className="london-floating-cta"
        onClick={() => {
          window.location.href = "/minibus-hire";
        }}
      >
        🚐 Book London Minibus / Coach
      </button>

      {/* BOTTOM CTA – MOBILE */}
      <div className="london-bottom-cta">
        <button
          className="london-bottom-cta-btn"
          onClick={() => {
            window.location.href = "/minibus-hire";
          }}
        >
          🚐 Book Minibus or Coach with CheapCabGrab
        </button>
      </div>
    </div>
  );
};

export default London;

